/**
 * A story source feeds the StoryScene one position at a time. Two
 * implementations exist, chosen at runtime (see main.ts):
 *
 *  - LocalStorySource — the offline animation preview. Navigation only, no
 *    authoritative scoring (see caseModel.ts).
 *  - RemoteStorySource (remoteSource.ts) — talks to @brhack/game-api, which
 *    verifies Telegram initData and runs the real shared engine.
 *
 * Both expose the same snapshot shape so the scene renders identically.
 */

import {
  StoryPlayback,
  reactionForChoice,
  type Ending,
  type Localized,
  type ReactionAnimation,
} from './caseModel.js';

export interface SnapshotChoice {
  readonly id: string;
  readonly label: Localized;
  /** The case's fictional console command, e.g. "/inspecter alerte-01". */
  readonly command?: Localized;
}

export interface StorySnapshot {
  readonly scene: {
    readonly id: string;
    readonly title: Localized;
    readonly body: Localized;
    readonly ending: Ending;
  };
  readonly choices: readonly SnapshotChoice[];
  readonly isEnding: boolean;
  readonly reaction?: Localized;
  /** Evidence collected so far, for the console readout. */
  readonly evidence: { readonly count: number; readonly total: number };
}

export interface ChooseResult {
  readonly snapshot: StorySnapshot;
  readonly reaction?: ReactionAnimation;
}

export interface StorySource {
  start(): Promise<StorySnapshot>;
  choose(choiceId: string): Promise<ChooseResult>;
  restart(): Promise<StorySnapshot>;
}

export class LocalStorySource implements StorySource {
  private readonly playback = new StoryPlayback();

  start(): Promise<StorySnapshot> {
    return Promise.resolve(this.snapshot());
  }

  choose(choiceId: string): Promise<ChooseResult> {
    const choice = this.playback.currentScene.choices.find(
      (candidate) => candidate.id === choiceId,
    );
    if (choice === undefined) throw new Error(`Unknown choice: ${choiceId}`);
    const reaction = reactionForChoice(choice);
    this.playback.choose(choice);
    const snapshot = this.snapshot(choice.reaction);
    return Promise.resolve(reaction === undefined ? { snapshot } : { snapshot, reaction });
  }

  restart(): Promise<StorySnapshot> {
    this.playback.restart();
    return Promise.resolve(this.snapshot());
  }

  private snapshot(reaction?: Localized): StorySnapshot {
    const scene = this.playback.currentScene;
    const base = {
      scene: { id: scene.id, title: scene.title, body: scene.body, ending: scene.ending },
      choices: this.playback.availableChoices().map((choice) => {
        const entry = { id: choice.id, label: choice.label };
        return choice.command === undefined ? entry : { ...entry, command: choice.command };
      }),
      isEnding: this.playback.isEnding,
      evidence: { count: this.playback.evidenceCount, total: this.playback.evidenceTotal },
    };
    return reaction === undefined ? base : { ...base, reaction };
  }
}
